import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';

import usePageTitle from '../../../hooks/usePageTitle';
import useSelectedResource from '../../../hooks/useSelectedResource';
import {useBreadcrumbs} from '../../../contexts/BreadcrumbContext';
import {axiosInstance} from '../../../routes/axiosInstance';
import {handleThunkError, selectResources} from '../../../slices/userSlice';
import Card, {CardHeader} from '../../../components/ui/Card';
import ConfirmDialog from '../../../components/ConfirmDialog';
import NoticesPanel from '../../../components/notices/NoticesPanel';
import {NOTICE_KIND, noticeKind} from '../../../components/notices/noticeKinds';
import {
  RESOURCE_TIMEZONE,
  formatDateTimeInResourceTz,
  getResourceTimezoneLabel,
  utcToZonedWallTime,
  zonedWallTimeToUtc,
} from '../../../utils/datetime';
import {findNextDstTransition} from '../../../utils/dstNotice';

const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 '
  + 'focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900';

const inputClass = 'w-full rounded-md border border-gray-300 dark:border-gray-600 '
  + 'bg-surface dark:bg-gray-870 px-2 py-1 text-sm text-gray-900 dark:text-gray-100 '
  + 'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

const buttonClass = 'inline-flex items-center px-3 py-1.5 rounded-md border '
  + 'border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-xs '
  + 'font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 '
  + 'dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed '
  + `transition-colors ${focusRing}`;

const labelClass = 'flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-300';

const AUDIENCES = {all: 'All users', forecasters: 'Forecasters', market_owners: 'Market owners'};
const MANUAL_KINDS = ['info', 'maintenance', 'critical'];

// Notice times are entered and shown in the resource timezone, labelled.
const formatWhen = (iso) => (iso
  ? `${formatDateTimeInResourceTz(iso)} ${getResourceTimezoneLabel(undefined, new Date(iso))}`
  : '');

const noticeStatus = (notice, now = Date.now()) => {
  if (new Date(notice.starts_at).getTime() > now) return 'Scheduled';
  if (notice.ends_at && new Date(notice.ends_at).getTime() <= now) return 'Ended';
  return 'Live';
};

const STATUS_CHIP = {
  Live: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  Scheduled: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  Ended: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

/**
 * Operations page where superusers post dashboard notices (maintenance,
 * announcements). Clock-change notices are automatic and only described here.
 */
export default function Notices() {
  usePageTitle('Notices');
  useBreadcrumbs(
    [{label: 'Operations', href: '/operations'}, {label: 'Notices'}],
    'Messages shown on the dashboard after login');

  // null while loading.
  const [notices, setNotices] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} (new) | notice
  const [pendingDelete, setPendingDelete] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const reload = useCallback(() => setListVersion((v) => v + 1), []);

  useEffect(() => {
    let stale = false;
    axiosInstance.get('/market/notices/')
      .then((response) => {
        if (!stale) setNotices(response.data.data || []);
      })
      .catch((error) => {
        if (stale) return;
        setNotices([]);
        toast.error(handleThunkError(error, 'Could not load the notices.'));
      });
    return () => {
      stale = true;
    };
  }, [listVersion]);

  const patch = (notice, fields, message) => axiosInstance
    .patch(`/market/notices/${notice.id}`, fields)
    .then(() => {
      toast.success(message);
      reload();
    })
    .catch((error) => toast.error(handleThunkError(error, 'Could not update the notice.')));

  const remove = (notice) => {
    setPendingDelete(null);
    axiosInstance.delete(`/market/notices/${notice.id}`)
      .then(() => {
        toast.success('Notice deleted.');
        reload();
      })
      .catch((error) => toast.error(handleThunkError(error, 'Could not delete the notice.')));
  };

  return (
    <div className="space-y-6">
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete notice"
        description={pendingDelete ? `"${pendingDelete.title}" disappears from the dashboard and this list.` : ''}
        confirmText="Delete notice"
        onConfirm={() => remove(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      />

      <AutomaticNoticesCard/>

      <Card>
        <CardHeader
          title="Manual notices"
          description="Shown above the dashboard to their audience between the start and end times (Brussels time)."
          actions={(
            <button type="button" className={buttonClass} onClick={() => setEditing({})}>
              New notice
            </button>
          )}
        />
        <NoticesTable
          notices={notices}
          onEdit={setEditing}
          onEnd={(notice) => patch(notice, {ends_at: new Date().toISOString()}, 'Notice ended.')}
          onDelete={setPendingDelete}
        />
      </Card>

      {editing && (
        <NoticeForm
          // key: switching notices starts from that notice's values.
          key={editing.id || 'new'}
          notice={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function AutomaticNoticesCard() {
  const resource = useSelectedResource();
  const timezone = resource?.timezone || RESOURCE_TIMEZONE;
  const next = useMemo(() => findNextDstTransition(timezone, new Date(), 400), [timezone]);

  return (
    <Card>
      <CardHeader
        title="Clock changes (automatic)"
        description="Nothing to write: the dashboard derives them from the selected resource's timezone."
      />
      <ul className="mt-3 list-disc pl-5 space-y-1 text-sm text-gray-500 dark:text-gray-400 max-w-3xl">
        <li>
          Shown from the 1st of the month of each daylight-saving switch until the end of the switch
          day, with the quarter-hours a day-ahead forecast for that day needs and, when Brussels
          switches too, the gate shift in UTC.
        </li>
        <li>
          {next ? (
            <>
              Next for {timezone}:{' '}
              <span className="font-mono text-gray-700 dark:text-gray-200">
                {formatDateTimeInResourceTz(next.at, timezone)}
              </span>
              {', '}
              {getResourceTimezoneLabel(timezone, new Date(next.at.getTime() - 1))}
              {' → '}
              {getResourceTimezoneLabel(timezone, next.at)}.
            </>
          ) : `${timezone} has no clock change in the next year.`}
        </li>
      </ul>
    </Card>
  );
}

function NoticesTable({notices, onEdit, onEnd, onDelete}) {
  if (notices === null) {
    return <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading notices...</p>;
  }
  if (notices.length === 0) {
    return <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No notices yet.</p>;
  }
  const actionClass = `rounded font-semibold hover:underline ${focusRing}`;
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200/70 dark:border-gray-600/70">
            <th className="py-2 pr-3 font-semibold">Notice</th>
            <th className="py-2 pr-3 font-semibold">Audience</th>
            <th className="py-2 pr-3 font-semibold">Shown</th>
            <th className="py-2 pr-3 font-semibold">Status</th>
            <th className="py-2"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {notices.map((notice) => {
            const status = noticeStatus(notice);
            return (
              <tr key={notice.id} className={status === 'Ended' ? 'opacity-60' : undefined}>
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-2">
                    <span aria-hidden="true" className={`h-2 w-2 flex-none rounded-full ${noticeKind(notice.kind).dot}`}/>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{notice.title}</span>
                  </div>
                  <div className="ml-4 text-xs text-gray-500 dark:text-gray-400">
                    {noticeKind(notice.kind).label} · by {notice.created_by_email || '-'}
                  </div>
                </td>
                <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                  {AUDIENCES[notice.audience]} · {notice.resource_name || 'all resources'}
                </td>
                <td className="py-2 pr-3 font-mono text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {formatWhen(notice.starts_at)}
                  <div>→ {notice.ends_at ? formatWhen(notice.ends_at) : 'until ended'}</div>
                </td>
                <td className="py-2 pr-3">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_CHIP[status]}`}>{status}</span>
                </td>
                <td className="py-2 text-right text-xs whitespace-nowrap space-x-3">
                  <button type="button" className={`${actionClass} text-brand-600 dark:text-brand-400`} onClick={() => onEdit(notice)}>
                    Edit
                  </button>
                  {status === 'Live' && (
                    <button type="button" className={`${actionClass} text-gray-600 dark:text-gray-300`} onClick={() => onEnd(notice)}>
                      End now
                    </button>
                  )}
                  <button type="button" className={`${actionClass} text-accent-600 dark:text-accent-400`} onClick={() => onDelete(notice)}>
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const emptyDraft = () => ({
  kind: 'info',
  title: '',
  summary: '',
  details: '',
  link_text: '',
  link_url: '',
  audience: 'all',
  resource: '',
  starts: utcToZonedWallTime(new Date()),
  ends: '',
});

function NoticeForm({notice, onCancel, onSaved}) {
  const resources = useSelector(selectResources) || [];
  const isNew = !notice.id;
  const [draft, setDraft] = useState(() => (isNew ? emptyDraft() : {
    ...notice,
    resource: notice.resource || '',
    starts: utcToZonedWallTime(notice.starts_at),
    ends: notice.ends_at ? utcToZonedWallTime(notice.ends_at) : '',
  }));
  const [saving, setSaving] = useState(false);
  const set = (field) => (event) => setDraft((current) => ({...current, [field]: event.target.value}));

  const startsAt = zonedWallTimeToUtc(draft.starts);
  const endsAt = draft.ends ? zonedWallTimeToUtc(draft.ends) : null;
  const canSave = draft.title.trim() && draft.summary.trim() && startsAt && !saving;

  const save = () => {
    setSaving(true);
    const payload = {
      kind: draft.kind,
      title: draft.title.trim(),
      summary: draft.summary.trim(),
      details: draft.details.trim(),
      link_text: draft.link_text.trim(),
      link_url: draft.link_url.trim(),
      audience: draft.audience,
      resource: draft.resource || null,
      starts_at: startsAt.toISOString(),
      ends_at: endsAt ? endsAt.toISOString() : null,
    };
    const request = isNew
      ? axiosInstance.post('/market/notices/', payload)
      : axiosInstance.patch(`/market/notices/${notice.id}`, payload);
    request
      .then(() => {
        toast.success(isNew ? 'Notice saved.' : 'Notice updated; users who dismissed it see it again.');
        onSaved();
      })
      .catch((error) => toast.error(handleThunkError(error, 'Could not save the notice.')))
      .finally(() => setSaving(false));
  };

  // Echo each Brussels time in UTC so the window is unambiguous.
  const utcEcho = (date) => (date ? `${formatDateTimeInResourceTz(date, 'UTC')} UTC · ${getResourceTimezoneLabel(undefined, date)}` : '');
  const preview = [{
    ...draft,
    id: 'preview',
    title: draft.title || 'Title',
    summary: draft.summary || 'One-line summary',
  }];

  return (
    <Card>
      <CardHeader title={isNew ? 'New notice' : 'Edit notice'} description="The preview updates as you type."/>
      <div className="mt-4 grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <fieldset>
            <legend className="text-xs text-gray-600 dark:text-gray-300">Kind</legend>
            <div className="mt-1 flex flex-wrap gap-3">
              {MANUAL_KINDS.map((kind) => (
                <label key={kind} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
                  <input type="radio" name="notice-kind" value={kind} checked={draft.kind === kind}
                    onChange={set('kind')} className="text-brand-600 focus:ring-brand-500"/>
                  <span aria-hidden="true" className={`h-2 w-2 rounded-full ${NOTICE_KIND[kind].dot}`}/>
                  {NOTICE_KIND[kind].label}
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Critical notices cannot be dismissed and are listed first.
            </p>
          </fieldset>
          <label className={labelClass}>
            Title
            <input type="text" maxLength={120} value={draft.title} onChange={set('title')} className={inputClass}
              placeholder="e.g. Planned maintenance Tue 20 Oct, 06:00-07:00 CEST"/>
          </label>
          <label className={labelClass}>
            Summary (one line, shown folded)
            <input type="text" maxLength={300} value={draft.summary} onChange={set('summary')} className={inputClass}/>
          </label>
          <label className={labelClass}>
            Details (optional, one bullet per line)
            <textarea rows={3} value={draft.details} onChange={set('details')} className={inputClass}/>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className={labelClass}>
              Link text
              <input type="text" maxLength={60} value={draft.link_text} onChange={set('link_text')} className={inputClass}/>
            </label>
            <label className={labelClass}>
              Link URL
              <input type="url" value={draft.link_url} onChange={set('link_url')} className={inputClass}
                placeholder="https://"/>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className={labelClass}>
              Audience
              <select value={draft.audience} onChange={set('audience')} className={inputClass}>
                {Object.entries(AUDIENCES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className={labelClass}>
              Resource
              <select value={draft.resource} onChange={set('resource')} className={inputClass}>
                <option value="">All resources</option>
                {resources.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                {draft.resource && !resources.some((r) => r.id === draft.resource) && (
                  <option value={draft.resource}>{notice.resource_name || draft.resource}</option>
                )}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className={labelClass}>
              Show from (Brussels)
              <input type="datetime-local" value={draft.starts} onChange={set('starts')} className={`${inputClass} font-mono`}/>
              <span className="font-mono text-gray-500 dark:text-gray-400">{utcEcho(startsAt)}</span>
            </label>
            <label className={labelClass}>
              Show until (Brussels, optional)
              <input type="datetime-local" value={draft.ends} onChange={set('ends')} className={`${inputClass} font-mono`}/>
              <span className="font-mono text-gray-500 dark:text-gray-400">{endsAt ? utcEcho(endsAt) : 'Until ended'}</span>
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className={buttonClass} onClick={onCancel}>Cancel</button>
            <button type="button" className={buttonClass} disabled={!canSave} onClick={save}>
              {isNew ? 'Save notice' : 'Save changes'}
            </button>
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-600 dark:text-gray-300">Preview on the dashboard</div>
          <div className="mt-1 rounded-xl border border-dashed border-gray-300 p-4 dark:border-gray-600">
            <NoticesPanel notices={preview}/>
          </div>
        </div>
      </div>
    </Card>
  );
}
